import { ChevronRight, Pencil, Plus, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useCreateNoteMutation,
  useDeleteNoteMutation,
  useGetNotesQuery,
  useGetOneToOneNoteQuery,
  useUpdateNoteMutation,
} from '@/services/api/matterNotesService';
import {
  BaseMatterNote,
  isOneToOneNoteType,
  NoteType,
} from '@/services/types/matter-notes-types';

import { MatterNoteForm } from '../../matter-notes-v2/form/MatterNoteForm';
import { MatterNoteConfig, NoteCardinality } from '../../matter-notes-v2/types';

interface PeopleSectionConfig {
  noteType: NoteType;
  config: MatterNoteConfig;
  icon: React.ReactNode;
  emptyTitle: string;
  emptyDescription: string;
  addButtonLabel: string;
}

interface PeopleSectionCardProps {
  matterId: string;
  section: PeopleSectionConfig;
}

export const PeopleSectionCard: React.FC<PeopleSectionCardProps> = ({
  matterId,
  section,
}) => {
  const [showNewForm, setShowNewForm] = useState(false);
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
  const {
    config,
    noteType,
    icon,
    emptyTitle,
    emptyDescription,
    addButtonLabel,
  } = section;

  const isOneToOne = isOneToOneNoteType(noteType);
  const isSingleCardinality = config.cardinality === NoteCardinality.SINGLE;

  // Use appropriate query based on note type
  const {
    data: oneToOneNote,
    isLoading: isLoadingOneToOne,
    refetch: refetchOneToOne,
  } = useGetOneToOneNoteQuery({ matterId, noteType }, { skip: !isOneToOne });

  const {
    data: manyNotes,
    isLoading: isLoadingMany,
    refetch: refetchMany,
  } = useGetNotesQuery({ matterId, noteType }, { skip: isOneToOne });

  const [deleteNote, { isLoading: isDeleting }] = useDeleteNoteMutation();

  const isLoading = isOneToOne ? isLoadingOneToOne : isLoadingMany;
  const refetch = isOneToOne ? refetchOneToOne : refetchMany;

  // Get the note(s) based on type
  const singleNote = isOneToOne
    ? (oneToOneNote as BaseMatterNote | undefined) || null
    : null;
  const notesList = !isOneToOne
    ? (manyNotes as BaseMatterNote[] | undefined)
    : undefined;
  const hasNotes = isOneToOne
    ? !!singleNote
    : notesList && notesList.length > 0;

  const handleAddNew = () => {
    setShowNewForm(true);
    setExpandedNoteId(null);
  };

  const handleSaved = () => {
    setShowNewForm(false);
    setExpandedNoteId(null);
    refetch();
  };

  const handleCancelled = () => {
    setShowNewForm(false);
  };

  const handleDeleted = () => {
    setExpandedNoteId(null);
    refetch();
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteNote({
        matterId,
        noteId,
        noteType,
      }).unwrap();
      toast.success('Deleted successfully');
      handleDeleted();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 flex items-center gap-3">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  // For single cardinality notes
  if (isSingleCardinality) {
    // Empty state
    if (!singleNote && !showNewForm) {
      return (
        <button
          type="button"
          onClick={handleAddNew}
          className="w-full rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 px-4 py-3 flex items-center gap-3 hover:border-zinc-400 hover:bg-zinc-50 transition-all group"
        >
          <span className="text-zinc-400 group-hover:text-zinc-500">
            {icon}
          </span>
          <span className="text-sm text-zinc-500 group-hover:text-zinc-600">
            {emptyTitle}
          </span>
          <Plus className="h-4 w-4 text-zinc-400 group-hover:text-zinc-500 ml-auto" />
        </button>
      );
    }

    // Show form
    return (
      <div className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden">
        <MatterNoteForm
          config={config}
          matterId={matterId}
          note={singleNote}
          onSaved={handleSaved}
          onCancelled={handleCancelled}
          onDeleted={handleDeleted}
        />
      </div>
    );
  }

  // For multiple cardinality notes - new clean design
  return (
    <div className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden">
      {/* Compact Header */}
      <div className="px-4 py-2.5 flex items-center justify-between border-b border-zinc-100">
        <div className="flex items-center gap-2.5">
          <span className="text-zinc-400">{icon}</span>
          <h3 className="text-sm font-medium text-zinc-700">{config.title}</h3>
          {hasNotes && notesList && (
            <span className="text-[10px] font-medium text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-full">
              {notesList.length}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleAddNew}
          disabled={showNewForm}
          className="h-6 px-2 text-xs text-zinc-500 hover:text-zinc-700"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* New form - slides in at top */}
      {showNewForm && (
        <div className="border-b border-zinc-200 bg-blue-50/50">
          <div className="px-4 py-2 flex items-center justify-between bg-blue-100/50 border-b border-blue-200/50">
            <span className="text-xs font-medium text-blue-700">
              New {config.title.replace(/s$/, '')}
            </span>
            <button
              type="button"
              onClick={handleCancelled}
              className="text-blue-600 hover:text-blue-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <CompactNoteForm
            config={config}
            matterId={matterId}
            onSaved={handleSaved}
            onCancelled={handleCancelled}
          />
        </div>
      )}

      {/* Notes list */}
      {hasNotes && notesList && (
        <div className="divide-y divide-zinc-100">
          {notesList.map((note) => (
            <NoteListItem
              key={note.id}
              note={note}
              config={config}
              matterId={matterId}
              isExpanded={expandedNoteId === note.id}
              onToggle={() =>
                setExpandedNoteId(expandedNoteId === note.id ? null : note.id)
              }
              onSaved={handleSaved}
              onDelete={() => handleDeleteNote(note.id)}
              isDeleting={isDeleting}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!hasNotes && !showNewForm && (
        <button
          type="button"
          onClick={handleAddNew}
          className="w-full px-4 py-6 flex flex-col items-center gap-2 hover:bg-zinc-50 transition-colors"
        >
          <span className="text-xs text-zinc-400">{emptyDescription}</span>
          <span className="text-xs font-medium text-primary">
            {addButtonLabel}
          </span>
        </button>
      )}
    </div>
  );
};

// Compact inline form for new notes
interface CompactNoteFormProps {
  config: MatterNoteConfig;
  matterId: string;
  note?: BaseMatterNote | null;
  onSaved: () => void;
  onCancelled: () => void;
}

const CompactNoteForm: React.FC<CompactNoteFormProps> = ({
  config,
  matterId,
  note,
  onSaved,
  onCancelled,
}) => {
  const [formValues, setFormValues] = useState<Record<string, any>>(
    note || { matter: matterId },
  );
  const [createNote, { isLoading: isCreating }] = useCreateNoteMutation();
  const [updateNote, { isLoading: isUpdating }] = useUpdateNoteMutation();

  const handleFieldChange = (name: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      if (note?.id) {
        await updateNote({
          matterId,
          noteId: note.id,
          noteType: config.type as NoteType,
          note: formValues,
        }).unwrap();
        toast.success('Updated successfully');
      } else {
        await createNote({
          matterId,
          noteType: config.type as NoteType,
          note: formValues,
        }).unwrap();
        toast.success('Created successfully');
      }
      onSaved();
    } catch (error: unknown) {
      // Extract error message from API response
      const apiError = error as {
        data?: { detail?: string; [key: string]: unknown };
      };
      if (apiError?.data) {
        const errorData = apiError.data;
        if (errorData.detail) {
          toast.error(errorData.detail);
        } else {
          // Check for field-specific errors
          const fieldErrors = Object.entries(errorData)
            .filter(([key]) => key !== 'detail')
            .map(([key, value]) => {
              const fieldConfig = config.fields.find((f) => f.name === key);
              const fieldLabel = fieldConfig?.label || key;
              const errorMsg = Array.isArray(value) ? value[0] : value;
              return `${fieldLabel}: ${errorMsg}`;
            });
          if (fieldErrors.length > 0) {
            toast.error(fieldErrors.join(', '));
          } else {
            toast.error('Failed to save. Please check your input.');
          }
        }
      } else {
        toast.error('Failed to save. Please try again.');
      }
    }
  };

  // Separate regular fields and multiline fields
  const regularFields = config.fields.filter((f) => !f.multiline);
  const multilineFields = config.fields.filter((f) => f.multiline);

  return (
    <div className="p-3 space-y-3">
      {/* Regular fields in a grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {regularFields.map((field) => (
          <div key={field.name}>
            <label
              htmlFor={`inline-field-${field.name}`}
              className="block text-[10px] font-medium text-zinc-500 mb-1"
            >
              {field.label}
            </label>
            {field.type === 'checkbox' ? (
              <label
                htmlFor={`inline-checkbox-${field.name}`}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  id={`inline-checkbox-${field.name}`}
                  type="checkbox"
                  checked={!!formValues[field.name]}
                  onChange={(e) =>
                    handleFieldChange(field.name, e.target.checked)
                  }
                  className="h-4 w-4 rounded border-zinc-300 text-primary focus:ring-primary"
                />
                <span className="text-xs text-zinc-600">{field.label}</span>
              </label>
            ) : (
              <input
                id={`inline-field-${field.name}`}
                type={field.type === 'date' ? 'date' : 'text'}
                value={formValues[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                placeholder={field.label}
                className="w-full px-2 py-1.5 text-xs border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            )}
          </div>
        ))}
      </div>
      {/* Multiline fields (like description) take full width */}
      {multilineFields.map((field) => (
        <div key={field.name}>
          <label
            htmlFor={`inline-textarea-${field.name}`}
            className="block text-[10px] font-medium text-zinc-500 mb-1"
          >
            {field.label}
          </label>
          <textarea
            id={`inline-textarea-${field.name}`}
            value={formValues[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.label}
            rows={3}
            className="w-full px-2 py-1.5 text-xs border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none"
          />
        </div>
      ))}
      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancelled}
          disabled={isCreating || isUpdating}
          className="h-7 text-xs"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isCreating || isUpdating}
          className="h-7 text-xs"
        >
          {isCreating || isUpdating ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// Individual note list item with expand/collapse
interface NoteListItemProps {
  note: BaseMatterNote;
  config: MatterNoteConfig;
  matterId: string;
  isExpanded: boolean;
  onToggle: () => void;
  onSaved: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

const NoteListItem: React.FC<NoteListItemProps> = ({
  note,
  config,
  matterId,
  isExpanded,
  onToggle,
  onSaved,
  onDelete,
  isDeleting,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  // Get title using formatTitle or first non-empty field
  const getDisplayTitle = () => {
    if (config.formatTitle) {
      return config.formatTitle(note as Record<string, any>) || 'Untitled';
    }
    // Find first non-empty field value
    const noteData = note as Record<string, any>;
    const firstField = config.fields.find(
      (field) =>
        noteData[field.name] &&
        typeof noteData[field.name] === 'string' &&
        noteData[field.name].trim(),
    );
    return firstField ? noteData[firstField.name] : 'Untitled';
  };

  // Get subtitle (second field value or description snippet)
  const getSubtitle = () => {
    const noteData = note as Record<string, any>;
    // Try to get a meaningful subtitle from other fields
    const subtitleField = config.fields
      .slice(1, 3)
      .find(
        (field) =>
          noteData[field.name] &&
          typeof noteData[field.name] === 'string' &&
          noteData[field.name].trim() &&
          !field.multiline,
      );
    if (subtitleField) {
      return noteData[subtitleField.name];
    }
    if (noteData.description) {
      return (
        noteData.description.substring(0, 50) +
        (noteData.description.length > 50 ? '...' : '')
      );
    }
    return null;
  };

  const title = getDisplayTitle();
  const subtitle = getSubtitle();

  if (isEditing) {
    return (
      <div className="bg-amber-50/50 border-l-2 border-amber-400">
        <div className="px-4 py-2 flex items-center justify-between bg-amber-100/50 border-b border-amber-200/50">
          <span className="text-xs font-medium text-amber-700">Editing</span>
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="text-amber-600 hover:text-amber-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <CompactNoteForm
          config={config}
          matterId={matterId}
          note={note}
          onSaved={() => {
            setIsEditing(false);
            onSaved();
          }}
          onCancelled={() => setIsEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="group">
      {/* Collapsed row */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="px-4 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-zinc-50 transition-colors"
        onClick={onToggle}
      >
        <ChevronRight
          className={`h-4 w-4 text-zinc-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-800 truncate">
            {title}
          </div>
          {subtitle && !isExpanded && (
            <div className="text-xs text-zinc-500 truncate">{subtitle}</div>
          )}
        </div>
        {/* Action buttons - visible on hover */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // eslint-disable-next-line no-alert, no-restricted-globals
              if (confirm('Delete this item?')) {
                onDelete();
              }
            }}
            disabled={isDeleting}
            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-3 pl-11 space-y-1.5 bg-zinc-50/50">
          {config.fields.map((field) => {
            const value = (note as Record<string, any>)[field.name];
            if (!value) return null;
            return (
              <div key={field.name} className="flex gap-2">
                <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-wide w-24 flex-shrink-0">
                  {field.label}
                </span>
                <span
                  className={`text-xs text-zinc-700 ${field.multiline ? 'whitespace-pre-wrap' : ''}`}
                >
                  {typeof value === 'boolean'
                    ? value
                      ? 'Yes'
                      : 'No'
                    : String(value)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
